/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import { IComponent, IComponentProps, getCatalogId, msdyn365Commerce } from '@msdyn365-commerce/core';
import {
    addOrderTemplateToCartAction,
    AddOrderTemplateToCartInput,
    OrderTemplate,
    validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    IPopupProductsProps,
    ITelemetryContent,
    PopupProducts,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React, { useState } from 'react';

import {
    ILinesAddedToCartDialogResources,
    LinesAddedToCartDialog
} from '../lines-added-to-cart-dialog/lines-added-to-cart-dialog.component';

export interface IAddOrderTemplateToCartComponentData {
    orderTemplate: OrderTemplate;
}

export interface IAddOrderTemplateToCartComponentProps extends IComponentProps<IAddOrderTemplateToCartComponentData> {
    className?: string;
    parentElementRef?: React.RefObject<Button>;
    addToCartText: string;
    telemetryContent?: ITelemetryContent;
    dialogStrings: {
        closeWindowButtonText: string;
        addToCartFailureMessage: string;
        addToCartSuccessMessage: string;
        addToCartProcessMessage: string;
        orderTemplateErrorMessage: string;
        linesAddedToCartDialogResources: ILinesAddedToCartDialogResources;
    };
    isMultipleCatalogFeatureEnabledInHq?: boolean;
    catalogId?: number;
}

export declare type IAddToCartActionFailureReason = 'EMPTYINPUT' | 'MISSINGDIMENSION' | 'OUTOFSTOCK' | 'CARTACTIONFAILED'; // Rename to used method

export interface IAddOrderTemplateToCartComponent extends IComponent<IAddOrderTemplateToCartComponentProps> {
    onClick(): (event: React.MouseEvent<HTMLElement>, props: IAddOrderTemplateToCartComponentProps) => void;
}

const onClick = async (
    _event: React.MouseEvent<HTMLElement>,
    props: IAddOrderTemplateToCartComponentProps,
    setUploadStatus: (uploadStatus: addOrderTemplateToCartStatus) => void,
    openModal: (opened: boolean) => void,
    setLinesAddedToCartDialogOpen: (opened: boolean) => void,
    setAddedLinesCount: (value: number) => void,
    setErrorMessage: (message: string) => void
): Promise<void> => {
    openModal(true);
    setUploadStatus('LOADING');
    const { context, dialogStrings } = props;
    // assuming selected catalogId will be passed to component and if not passed then get from context
    const catalogId = props.catalogId ?? getCatalogId(props.context.actionContext.requestContext);
    validateCatalogId(catalogId);
    const isMultipleCatalogFeatureEnabledInHq = props.isMultipleCatalogFeatureEnabledInHq || false;
    const addOrderTemplateToCartInput = isMultipleCatalogFeatureEnabledInHq
        ? new AddOrderTemplateToCartInput(props.data.orderTemplate.productList.Id, isMultipleCatalogFeatureEnabledInHq)
        : new AddOrderTemplateToCartInput(props.data.orderTemplate.productList.Id);
    try {
        const addedCartLines = await addOrderTemplateToCartAction(addOrderTemplateToCartInput, context.actionContext);
        setAddedLinesCount(addedCartLines.length);
        openModal(false);
        setTimeout(() => {
            setLinesAddedToCartDialogOpen(true);
        }, 500);
        setErrorMessage('');
    } catch (error) {
        setErrorMessage(dialogStrings.orderTemplateErrorMessage);
        setUploadStatus('FAILED');
    }
};

const AddToCartComponentActions = {
    onClick
};

export type addOrderTemplateToCartStatus = 'LOADING' | 'SUCCESS' | 'FAILED' | 'WAITING';

const _getModalText = (status: addOrderTemplateToCartStatus, props: IAddOrderTemplateToCartComponentProps, errorMsg: string) => {
    const { dialogStrings } = props;
    const { addToCartFailureMessage, addToCartSuccessMessage, addToCartProcessMessage } = dialogStrings;

    if (status === 'LOADING') {
        return `${addToCartProcessMessage}...`;
    }

    if (status === 'SUCCESS') {
        return addToCartSuccessMessage;
    }

    if (status === 'FAILED') {
        return errorMsg !== null ? errorMsg : addToCartFailureMessage;
    }

    return '';
};

let buttonEventReference: HTMLButtonElement | undefined;

const AddOrderTemplate: React.FC<IAddOrderTemplateToCartComponentProps> = (props: IAddOrderTemplateToCartComponentProps) => {
    const [uploadStatus, setUploadStatus] = useState<addOrderTemplateToCartStatus>('WAITING');
    const [modalOpen, setModalOpen] = useState(false);
    const [addedLinesCount, setAddedLinesCount] = useState(0);
    const [linesAddedToCartDialogOpen, setLinesAddedToCartDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const onClickHandler = async (event: React.MouseEvent<HTMLElement>) => {
        buttonEventReference = event.target as HTMLButtonElement;
        await AddToCartComponentActions.onClick(
            event,
            props,
            setUploadStatus,
            setModalOpen,
            setLinesAddedToCartDialogOpen,
            setAddedLinesCount,
            setErrorMessage
        );
    };
    const closeLinesAddedToCartDialog = () => {
        setLinesAddedToCartDialogOpen(false);
        setErrorMessage('');
        buttonEventReference?.focus();
    };

    const modalText = _getModalText(uploadStatus, props, errorMessage);
    const processStatusClass = uploadStatus.toLowerCase();
    const dialogStrings = { closeWindowButtonText: props.dialogStrings.closeWindowButtonText };
    const payLoad = getPayloadObject('click', props.telemetryContent!, TelemetryConstant.AddOrderTemplateToCart);
    const addOrderTemplateAttribute = getTelemetryAttributes(props.telemetryContent!, payLoad);

    const popupProps: IPopupProductsProps = {
        dialogStrings,
        className: 'msc-add-order-template-to-cart',
        processStatusClass,
        text: modalText,
        modalOpen,
        setModalOpen
    };

    const renderModalPopup = <PopupProducts {...popupProps} />;

    const products = props.data.orderTemplate.orderTemplateLines
        .map(line => line.fullProduct)
        .filter(fullProduct => fullProduct) as FullProduct[];
    const isMultipleCatalogFeatureEnabledInHq = props.isMultipleCatalogFeatureEnabledInHq || false;
    return (
        <>
            {renderModalPopup}
            <button
                className={classnames(
                    'msc-add-order-template-to-cart msc-btn',
                    props.className,
                    props.data.orderTemplate.totalLines === 0 ? 'disabled' : ''
                )}
                aria-label={props.addToCartText}
                disabled={props.data.orderTemplate.totalLines === 0}
                type='button'
                onClick={onClickHandler}
                {...addOrderTemplateAttribute}
            >
                {props.addToCartText}
            </button>
            <LinesAddedToCartDialog
                className='msc-lines-added-to-cart-dialog'
                id={props.id}
                typeName={props.typeName}
                context={props.context}
                dialogStrings={props.dialogStrings.linesAddedToCartDialogResources}
                data={{
                    linesCount: addedLinesCount,
                    orderTemplate: props.data.orderTemplate.productList,
                    products
                }}
                modalOpen={linesAddedToCartDialogOpen}
                onClose={closeLinesAddedToCartDialog}
                isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogFeatureEnabledInHq || false}
                {...(isMultipleCatalogFeatureEnabledInHq && { catalogId: props.catalogId })}
            />
        </>
    );
};

export const AddOrderTemplateToCartComponent: React.FunctionComponent<IAddOrderTemplateToCartComponentProps> =
    // @ts-expect-error -- As per design, custom component
    msdyn365Commerce.createComponent<IAddOrderTemplateToCartComponent>('AddOrderTemplate', {
        component: AddOrderTemplate,
        ...AddToCartComponentActions
    });
